import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * One-tap voice dictation: records, sends to Whisper, returns text via onText.
 * Zero typing required anywhere this button appears.
 */
export function DictateButton({ onText, testid = "dictate-btn", className = "" }) {
  const [state, setState] = useState("idle"); // idle | recording | busy
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("busy");
        const fd = new FormData();
        fd.append("audio", new Blob(chunksRef.current, { type: "audio/webm" }), "dictation.webm");
        try {
          const res = await api.transcribe(fd);
          if (res.text?.trim()) onText(res.text.trim());
          else toast("Didn't catch that — try again");
        } catch {
          toast.error("Transcription failed");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      mediaRef.current = mr;
      setState("recording");
    } catch {
      toast.error("Microphone unavailable");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={state === "recording" ? stop : state === "idle" ? start : undefined}
      aria-label={state === "recording" ? "Stop dictation" : "Dictate with your voice"}
      className={`shrink-0 h-10 w-10 rounded-full grid place-items-center transition-colors ${
        state === "recording"
          ? "bg-red-600 text-white animate-pulse"
          : "border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
      } ${className}`}
    >
      {state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        : state === "recording" ? <Square className="h-4 w-4 fill-current" strokeWidth={1.5} />
        : <Mic className="h-4 w-4" strokeWidth={1.5} />}
    </button>
  );
}
